import React, { useState } from 'react';
import Icon from '../../Components/Icons/Icon';
import menu from '../menu';
import { Link, useLocation } from 'react-router-dom';
import { useSession } from '../../Components/Session';
import { useHelp } from '../../Components/Help';
import { useDialogues } from '../../Components/Dialogues';
import { classNames, useFlipFlop } from '../../Services/helpers';
import { levels } from '../../Data/Admin';
import { useAssistantContext } from '../../Components/Assistant';
import Status from '../../Components/Offline/Status';

export default () => {
    const path = useLocation().pathname;
    const activeTab = menu.items.find((item) => path.match(item.match));
    const subtabs = activeTab?.subitems ?? [];

    const [previousTabs, setPreviousTabs] = useState({});

    const {notificationBadge, hasFeature, isRoleSufficient, isAdminSwitched, isAdmin} = useSession();
    const {unreadMessages} = useHelp();

    return <div className="fixed bottom-0 left-0 w-full shadow-lg z-20" style={{boxShadow: '0px 0px 5px 0px rgba(0,0,0,0.25)'}}>

        <AdminSwitchBack />
        <Status className="text-center py-1 text-xs" />

        <div className="bg-white shadow-lg text-brand" id="tab-container">

            <div className={classNames(
                'flex flex-row text-center overflow-x-auto text-sm border border-b transition-all duration-300',
                subtabs?.length > 0 ? 'h-[2rem] opacity-100' : 'h-0 opacity-0',
            )}>
                {subtabs.map((item) => {
                    if (item.adminOnly && !isAdmin(levels.READ_ONLY)) {
                        return null;
                    }

                    return <Subtab key={item.title}
                                title={item.title}
                                path={item.path}
                                active={path.match(item.match)}
                                feature={item.feature}
                                role={item.role}
                                setPreviousTab={tab => setPreviousTabs(prev => ({...prev, [activeTab.title]: tab}))}
                            />
                })}
            </div>

            <div className="flex flex-row text-center">
                {menu.items.map((item) => {
                    if (item.adminOnly && !isAdmin(levels.READ_ONLY)) {
                        return null;
                    }

                    if (item.role && !isRoleSufficient(item.role)) {
                        return <LockedTab key={item.title} title={item.title} icon={item.icon} role={item.role} />;
                    }

                    let badge = false;
                    if (item.notificationBadge) {
                        badge = notificationBadge ? notificationBadge : false;
                    } else if (item.helpMessageBadge) {
                        badge = unreadMessages ? unreadMessages : false;
                    }

                    return <Tab key={item.title}
                                title={item.title}
                                icon={item.icon}
                                path={selectTabPath(item, hasFeature)}
                                active={path.match(item.match)}
                                badge={badge}
                                isAdminSwitched={isAdminSwitched}
                                previousTab={previousTabs[item.title]}
                            />
                })}
                <Assistant />
            </div>
        </div>
    </div>
}

const AdminSwitchBack = () => {
    const {isAdmin, isAdminSwitched, adminSwitchBack} = useSession();

    if (!isAdmin() || !isAdminSwitched) {
        return null;
    }

    return <div className="bg-red-500 text-white font-bold text-center py-1 text-xs"
        onClick={() => adminSwitchBack()}
    >
        Switch back
    </div>
}

const Assistant = () => {
    const { assistantOpen, setAssistantOpen, unseen} = useAssistantContext();
    const {isAdminSwitched} = useSession();
    const bounce = useFlipFlop(2000, 10000);

    if (isAdminSwitched) {
        return null;
    }

    return <div className={classNames(
            'w-1/5 pt-1 pb-1',
            (unseen || assistantOpen) ? 'text-brand' : 'text-muted',
            )}
            onClick={() => setAssistantOpen(!assistantOpen)}
        >
        <div className="h-7 w-6 m-auto relative mb-1">
            <Icon icon={(assistantOpen || unseen) ? "SidSolid" : "SidLight"}
                theme="soapy"
                size="3x"
                badge={unseen}
                className={(bounce && unseen) ? 'animate__animated animate__bounce' : ''}
            />
        </div>
        <span className="block text-xs">Sid</span>
    </div>
}

const selectTabPath = (item, hasFeature) => {
    return item.path ?? item.subitems.find((subitem) => !subitem.feature || hasFeature(subitem.feature))?.path;
}

const Tab = ({ title, icon, path, active, badge, previousTab }) => {

    return <Link to={previousTab ?? path} className={classNames(
            'w-1/5 pt-2 pb-1', // border-b-4
            active ? 'text-brand fill-brand font-bold' : 'text-muted fill-muted',
        )}>
        <div className="h-7 w-6 m-auto relative">
            <Icon icon={icon} theme={active ? 'solid' : 'light'} size="2x" badge={badge} />
        </div>
        <span className="block text-xs">{title}</span>
    </Link>
}

const LockedTab = ({ title, icon, role }) => {
    const {roleInsufficient} = useDialogues();
    return <div className={'w-1/5 py-2 fill-muted'} onClick={() => roleInsufficient(role)}>
        <div className="h-7 w-6 m-auto relative">
            <Icon icon={icon} theme="light" size="2x" />
        </div>
        <span className="block text-xs text-muted">{title}</span>
    </div>
}

const Subtab = ({ title, path, active, feature, role, setPreviousTab }) => {
    const {hasFeature, isRoleSufficient} = useSession();
    const {featureUnavailable, roleInsufficient} = useDialogues();

    let element = <Link to={path} className="block text-sm"
        onClick={() => setPreviousTab(path)}
    >
        {title}
    </Link>;

    if ((feature && !hasFeature(feature)) || (role && !isRoleSufficient(role))) {
        element = <span className="block text-s text-muted fill-muted break-keep text-nowrap"
            onClick={() => {
                if (feature && !hasFeature(feature)) {
                    featureUnavailable(feature);
                    return
                }
                if (role && !isRoleSufficient(role)) {
                    roleInsufficient(role);
                    return;
                }
            }}
        >
            {title} <Icon icon="Lock" className="ml-1" size="0.5x" inline />
        </span>;
    }

    return <div className={classNames(
            'grow shrink-0 py-1 px-2 max-h-[2rem] overflow-hidden',
            active ? 'font-bold text-brand' : 'text-muted',
        )}>
        {element}
    </div>
}
